import { useEffect, useMemo, useRef } from "react";
import { flagForCurrency, defaultCurrencyForCountry } from "../../common/commonUtils/currencyUtils";

const getCurrencySymbol = (code) => {
  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value || code;
  } catch {
    return code;
  }
};

const FlagIcon = ({ code }) =>
  code ? (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bs-tertiary-bg)",
        border: "1px solid var(--bs-border-color)",
        padding: 2,
      }}
    >
      <img
        src={`https://flagcdn.com/32x24/${code}.png`}
        alt=""
        width={28}
        height={20}
        loading="lazy"
        style={{ display: "block", objectFit: "cover", borderRadius: "50%" }}
      />
    </div>
  ) : null;

export default function CurrencyAmountInput({
  label,
  amount,
  onAmountChange,
  countryName,
  currency,
  onCurrencyChange,
  nameAmount = "amount",
  helper,
}) {
  const derivedCurrency = useMemo(() => {
    const fromCountry = defaultCurrencyForCountry(countryName);
    return (fromCountry || currency || "USD").toUpperCase();
  }, [countryName, currency]);

  const onChangeRef = useRef(onCurrencyChange);
  useEffect(() => {
    onChangeRef.current = onCurrencyChange;
  }, [onCurrencyChange]);

  const lastSentRef = useRef(null);
  useEffect(() => {
    const current = String(currency || "").toUpperCase();
    const next = String(derivedCurrency || "").toUpperCase();

    if (!next || next === current) return;

    if (lastSentRef.current === next) return;
    lastSentRef.current = next;

    onChangeRef.current?.(next);
  }, [derivedCurrency, currency]);

  const currencySymbol = getCurrencySymbol(derivedCurrency);
  const flagCode = flagForCurrency(derivedCurrency, countryName);

  const inputRef = useRef(null);
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const stopWheel = (e) =>
      el === document.activeElement && el.type === "number" && e.preventDefault();
    el.addEventListener("wheel", stopWheel, { passive: false });
    return () => el.removeEventListener("wheel", stopWheel);
  }, []);

  return (
    <div className="mb-3">
      {label && (
        <div className="d-flex align-items-center justify-content-between mb-2">
          <label className="form-label mb-0" htmlFor={nameAmount}>
            {label}
          </label>
          <div className="d-flex align-items-center gap-2">
            {countryName ? (
              <span className="badge bg-body-tertiary border text-body">{countryName}</span>
            ) : null}
            {derivedCurrency ? (
              <span className="badge bg-body-tertiary border text-body">{derivedCurrency}</span>
            ) : null}
          </div>
        </div>
      )}

      <div className="pt-2">
        <div className="d-flex align-items-center gap-2">
          <div className="input-group" style={{ flex: "1 1 0", minWidth: 0 }}>
            <span
              className="input-group-text d-flex align-items-center gap-2 bg-body"
              style={{
                height: 44,
                borderTopLeftRadius: 999,
                borderBottomLeftRadius: 999,
                fontWeight: 700,
                paddingInline: 12,
              }}
              aria-hidden="true"
            >
              <FlagIcon code={flagCode} />
              {currencySymbol}
            </span>
            <input
              ref={inputRef}
              id={nameAmount}
              name={nameAmount}
              type="number"
              inputMode="decimal"
              className="form-control text-start bg-body"
              placeholder="0.00"
              value={amount}
              onChange={(e) => onAmountChange?.(e.target.value)}
              min="0"
              step="1.0"
              required
              style={{
                height: 44,
                borderTopRightRadius: 999,
                borderBottomRightRadius: 999,
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                letterSpacing: ".2px",
              }}
            />
          </div>
        </div>

        <input type="hidden" name={`${nameAmount}Currency`} value={derivedCurrency} />

        {helper && <div className="form-text mt-2">{helper}</div>}
      </div>
    </div>
  );
}
